import React, { createContext, useContext, useEffect, useState } from 'react';

/** Réglages d'accessibilité client — taille de texte globale + contraste élevé, réglables
 * depuis "Profil" et appliqués à tout l'espace client (nutrition, profil, entraînements...).
 * Taille de texte : change le font-size racine (<html>), donc toutes les classes Tailwind
 * basées sur rem (quasi toutes) suivent automatiquement, sans toucher chaque composant.
 * Contraste élevé : attribut [data-contrast="high"] sur <html>, éclaircit les couleurs
 * "zinc/gray" mutées via les variables CSS déjà en place pour le thème clair/sombre (index.css). */
const TEXT_SCALE_KEY = 'bodyup_text_scale';
const CONTRAST_KEY = 'bodyup_high_contrast';
export const TEXT_SCALE_LEVELS = ['base', 'lg', 'xl'];
const TEXT_SCALE_PX = { base: 16, lg: 18, xl: 20 };

const ClientSettingsContext = createContext(null);

export const ClientSettingsProvider = ({ children }) => {
  const [textScale, setTextScale] = useState(() => {
    const saved = localStorage.getItem(TEXT_SCALE_KEY);
    return TEXT_SCALE_LEVELS.includes(saved) ? saved : 'base';
  });
  const [highContrast, setHighContrast] = useState(() => localStorage.getItem(CONTRAST_KEY) === '1');

  useEffect(() => {
    document.documentElement.style.fontSize = `${TEXT_SCALE_PX[textScale]}px`;
    localStorage.setItem(TEXT_SCALE_KEY, textScale);
    return () => { document.documentElement.style.fontSize = ''; };
  }, [textScale]);

  useEffect(() => {
    document.documentElement.setAttribute('data-contrast', highContrast ? 'high' : 'normal');
    localStorage.setItem(CONTRAST_KEY, highContrast ? '1' : '0');
    return () => { document.documentElement.removeAttribute('data-contrast'); };
  }, [highContrast]);

  const cycleTextScale = (dir) => {
    const idx = TEXT_SCALE_LEVELS.indexOf(textScale);
    const next = TEXT_SCALE_LEVELS[Math.min(TEXT_SCALE_LEVELS.length - 1, Math.max(0, idx + dir))];
    setTextScale(next);
  };

  return (
    <ClientSettingsContext.Provider value={{ textScale, setTextScale, cycleTextScale, highContrast, setHighContrast }}>
      {children}
    </ClientSettingsContext.Provider>
  );
};

export const useClientSettings = () => useContext(ClientSettingsContext) || {
  textScale: 'base', setTextScale: () => {}, cycleTextScale: () => {}, highContrast: false, setHighContrast: () => {},
};
